import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Check, ChevronLeft, Shield, CheckCircle2, PartyPopper, AlertTriangle, Loader2 } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import api from '../utils/api';
import { DEFAULT_PHONE_CODE } from '../constants/countries';
import { trackCompleteRegistration } from '../utils/trackingUtils';

// Constants for validation
const MAX_NAME_LENGTH = 100;
const MAX_EMAIL_LENGTH = 254;
const MAX_PHONE_LENGTH = 20;
const MIN_NAME_LENGTH = 2;

// Sanitize input - remove potentially dangerous characters
// Note: Don't trim here - we want to allow spaces between names during typing
const sanitizeInput = (str, maxLength = 100) => {
  if (!str) return '';
  return str
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/[<>'"&]/g, '') // Remove common injection chars
    .slice(0, maxLength);
};

// Final sanitize with trim - use this before submission
const sanitizeInputFinal = (str, maxLength = 100) => {
  if (!str) return '';
  return str
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/[<>'"&]/g, '') // Remove common injection chars
    .trim()
    .slice(0, maxLength);
};

// Validate email format
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return email && emailRegex.test(email) && email.length <= MAX_EMAIL_LENGTH;
};

// 6-digit code input component with auto-submit
const CodeInput = ({ value, onChange, disabled = false, onComplete }) => {
  const inputRefs = useRef([]);
  const digits = value.padEnd(6, '').split('').slice(0, 6);

  const handleChange = (index, e) => {
    const digit = e.target.value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    const newValue = newDigits.join('');
    onChange(newValue);
    
    // Auto-focus next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when 6 digits entered
    if (newValue.length === 6 && onComplete) {
      setTimeout(() => onComplete(newValue), 100);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pastedData);
    // Focus last filled input or first empty
    const focusIndex = Math.min(pastedData.length, 5);
    inputRefs.current[focusIndex]?.focus();
    
    // Auto-submit if 6 digits pasted
    if (pastedData.length === 6 && onComplete) {
      setTimeout(() => onComplete(pastedData), 100);
    }
  };

  return (
    <div className="flex gap-2 justify-center mb-6">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[index] || ''}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className="w-12 h-14 text-center text-2xl font-mono border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
          autoFocus={index === 0}
        />
      ))}
    </div>
  );
};

const MobileWaiverWizard = ({ trip, onClose, organizationName, orgBranding, customWaiverText, brandColor = '#2563EB', onSuccess, waiverVersion = '1.0', trackingConfig = {} }) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const signaturePadRef = useRef(null);
  
  // Store captured signature as base64 (captured when leaving step 2)
  // Small compressed JPEG ~2.7KB - stored directly in MongoDB with the waiver
  const [capturedSignatureData, setCapturedSignatureData] = useState(null);
  
  // Checkbox for agreeing to waiver
  const [hasAgreedToWaiver, setHasAgreedToWaiver] = useState(false);
  
  // Verification method: 'sms', 'email', or 'id'
  const [verificationMethod, setVerificationMethod] = useState(''); // '' = show selection, 'email', 'phone', 'id'
  
  // SMS verification
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCode, setSmsCode] = useState('');
  
  // Email verification
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  
  const [sendingCode, setSendingCode] = useState(false);
  const [signing, setSigning] = useState(false);
  const [waiverViewed, setWaiverViewed] = useState(false);
  const [showWaiverText, setShowWaiverText] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [skippedVerification, setSkippedVerification] = useState(false);
  const [lastSendTime, setLastSendTime] = useState(0);
  
  // Track failed verification methods for progressive fallback
  const [failedMethods, setFailedMethods] = useState([]); // ['email', 'phone']
  const [phoneError, setPhoneError] = useState('');
  
  // Refs to track latest code values (to avoid stale state in callbacks)
  const latestSmsCodeRef = useRef('');
  const latestEmailCodeRef = useRef('');
  
  // Success state
  const [showSuccess, setShowSuccess] = useState(false);
  const [signedPin, setSignedPin] = useState('');

  // Input validation errors
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Validate name input
  const validateName = (value) => {
    const sanitized = sanitizeInput(value, MAX_NAME_LENGTH);
    if (sanitized.length < MIN_NAME_LENGTH) {
      setNameError('Name must be at least 2 characters');
      return false;
    }
    if (!/^[a-zA-Z\s\-'.]+$/.test(sanitized)) {
      setNameError('Name contains invalid characters');
      return false;
    }
    setNameError('');
    return true;
  };

  // Validate email input
  const validateEmail = (value) => {
    if (!isValidEmail(value)) {
      setEmailError('Please enter a valid email');
      return false;
    }
    setEmailError('');
    return true;
  };

  // No need for separate upload - we'll send the small base64 directly with the waiver
  // ~2KB JPEG = ~2.7KB base64 - totally fine for MongoDB
  
  // Capture signature from canvas - optimize for small file size
  const captureSignatureLightweight = () => {
    if (!signaturePadRef.current) {
      return null;
    }
    
    if (signaturePadRef.current.isEmpty()) {
      return null;
    }
    
    try {
      // Get the canvas element directly
      const canvas = signaturePadRef.current.getCanvas();
      
      // Create a smaller canvas for the final image (max 300px wide)
      const maxWidth = 300;
      const maxHeight = 150;
      
      let targetWidth = canvas.width;
      let targetHeight = canvas.height;
      
      // Scale down if needed
      if (targetWidth > maxWidth) {
        const ratio = maxWidth / targetWidth;
        targetWidth = maxWidth;
        targetHeight = Math.round(canvas.height * ratio);
      }
      if (targetHeight > maxHeight) {
        const ratio = maxHeight / targetHeight;
        targetHeight = maxHeight;
        targetWidth = Math.round(targetWidth * ratio);
      }
      
      // Create optimized canvas
      const optimizedCanvas = document.createElement('canvas');
      optimizedCanvas.width = targetWidth;
      optimizedCanvas.height = targetHeight;
      const ctx = optimizedCanvas.getContext('2d');
      
      // White background (for JPEG compression)
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, targetWidth, targetHeight);
      
      // Draw the signature scaled down
      ctx.drawImage(canvas, 0, 0, targetWidth, targetHeight);
      
      // Export as JPEG with low quality (signatures don't need high quality)
      // This produces ~2KB which becomes ~2.7KB as base64 - perfect for MongoDB
      const dataUrl = optimizedCanvas.toDataURL('image/jpeg', 0.6);
      
      return dataUrl;
    } catch (error) {
      return null;
    }
  };

  const handleSendCode = async () => {
    const now = Date.now();
    const timeSinceLastSend = (now - lastSendTime) / 1000;
    if (timeSinceLastSend < 60 && lastSendTime > 0) {
      toast.error(`Please wait ${Math.ceil(60 - timeSinceLastSend)} seconds before sending again`);
      return;
    }

    setSendingCode(true);
    setVerificationFailed(false);
    
    try {
      // No upload needed - signature base64 will be sent directly with waiver
      // It's only ~2.7KB so it's fine to include in the payload
      
      if (verificationMethod === 'phone' || verificationMethod === 'sms') {
        const formatted = phoneNumber.startsWith('+') ? phoneNumber : `${DEFAULT_PHONE_CODE}${phoneNumber}`;
        await api.post('/public/sms/send-code', { phoneNumber: formatted });
        toast.success('SMS code sent!');
        setPhoneNumber(formatted);
      } else if (verificationMethod === 'email') {
        if (!validateEmail(email)) {
          setSendingCode(false);
          return;
        }
        await api.post('/public/email/send-code', { email: sanitizeInput(email, MAX_EMAIL_LENGTH) });
        toast.success('Email code sent! Check your inbox.');
      }
      
      setResendCooldown(60);
      setLastSendTime(now);
      setSkippedVerification(false);
      setStep(5);
    } catch (error) {
      setVerificationFailed(true);
      toast.error(`Failed to send ${verificationMethod === 'phone' || verificationMethod === 'sms' ? 'SMS' : 'email'} code`);
    } finally {
      setSendingCode(false);
    }
  };

  const handleSign = async () => {
    setSigning(true);
    try {
      let verified = false;
      let verifiedContact = '';
      
      // Use refs for latest code values to avoid stale state issues
      const currentSmsCode = latestSmsCodeRef.current || smsCode;
      const currentEmailCode = latestEmailCodeRef.current || emailCode;
      
      if ((verificationMethod === 'phone' || verificationMethod === 'sms') && phoneNumber && currentSmsCode) {
        const verifyRes = await api.post('/public/sms/verify-code', {
          phoneNumber: phoneNumber,
          code: currentSmsCode
        });
        verified = verifyRes.data.valid;
        verifiedContact = phoneNumber;
        
        if (!verified) {
          toast.error('Invalid SMS code');
          setSigning(false);
          return;
        }
      } else if (verificationMethod === 'email' && email && currentEmailCode) {
        const verifyRes = await api.post('/public/email/verify-code', {
          email: sanitizeInput(email, MAX_EMAIL_LENGTH),
          code: currentEmailCode
        });
        verified = verifyRes.data.valid;
        verifiedContact = email;
        
        if (!verified) {
          toast.error('Invalid email code');
          setSigning(false);
          return;
        }
      } else if (verificationMethod === 'id') {
        verified = true;
        verifiedContact = 'ID Uploaded';
      }

      const deviceInfo = {};
      const safe = (fn) => { try { return fn(); } catch { return 'Unknown'; } };
      deviceInfo.userAgent = safe(() => navigator.userAgent);
      deviceInfo.platform = safe(() => navigator.platform);
      deviceInfo.screenResolution = safe(() => `${window.screen.width}x${window.screen.height}`);
      deviceInfo.timezone = safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
      deviceInfo.language = safe(() => navigator.language);

      const pin = (trip.confirmationCode || trip.id).replace(/[^0-9]/g, '').slice(-3).padStart(3, '0');

      // Send signature as base64 directly - it's only ~2.7KB after compression
      const waiverPayload = {
        signature: sanitizeInputFinal(name, MAX_NAME_LENGTH),
        signatureName: sanitizeInputFinal(name, MAX_NAME_LENGTH),
        signatureData: capturedSignatureData || '', // Small JPEG base64, stored directly in MongoDB
        verified: verified,
        verificationMethod: verificationMethod === 'phone' ? 'sms' : verificationMethod, // Normalize 'phone' to 'sms' for consistency
        verifiedContact: verifiedContact,
        phoneNumber: (verificationMethod === 'sms' || verificationMethod === 'phone') ? phoneNumber : '',
        email: verificationMethod === 'email' ? sanitizeInputFinal(email, MAX_EMAIL_LENGTH) : '',
        deviceInfo: deviceInfo,
        pin: pin,
        signedAt: new Date().toISOString(),
        waiverVersion: waiverVersion
      };
      
      // Use v2 PostgreSQL endpoint
      await api.post(`/v2/public/bookings/${trip.confirmationCode || trip.id}/sign-waiver`, waiverPayload);

      // Track waiver completion for ad attribution
      if (trackingConfig.googleAdsId || trackingConfig.metaPixelId) {
        trackCompleteRegistration({
          googleAdsId: trackingConfig.googleAdsId,
          googleAdsLabel: trackingConfig.googleAdsLabel,
          metaPixelId: trackingConfig.metaPixelId,
          contentId: trip.confirmationCode || trip.id,
          contentName: `Waiver - ${organizationName || 'Booking'}`,
          customData: {
            waiver_version: waiverVersion,
            verification_method: verificationMethod
          }
        });
      }

      // Show success screen instead of immediate reload
      setSignedPin(pin);
      setShowSuccess(true);
    } catch (error) {
      
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data?.detail || '';
        
        if (status === 404) {
          toast.error('Booking not found. Please refresh the page.');
        } else if (status === 403) {
          toast.error('Access denied. Please refresh the page.');
        } else if (status === 400) {
          toast.error(`Invalid request: ${detail || 'Please check your information.'}`);
        } else {
          toast.error(`Failed to sign waiver (Error ${status})`);
        }
      } else if (error.request) {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error('Failed to sign waiver. Please try again.');
      }
    } finally {
      setSigning(false);
    }
  };

  const handleIdVerificationSign = async () => {
    setSigning(true);
    try {
      const deviceInfo = {};
      const safe = (fn) => { try { return fn(); } catch { return 'Unknown'; } };
      deviceInfo.userAgent = safe(() => navigator.userAgent);
      deviceInfo.platform = safe(() => navigator.platform);
      deviceInfo.screenResolution = safe(() => `${window.screen.width}x${window.screen.height}`);
      deviceInfo.timezone = safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone);

      const pin = (trip.confirmationCode || trip.id).replace(/[^0-9]/g, '').slice(-3).padStart(3, '0');
      
      // Use v2 PostgreSQL endpoint
      await api.post(`/v2/public/bookings/${trip.confirmationCode || trip.id}/sign-waiver`, {
        signature: sanitizeInputFinal(name, MAX_NAME_LENGTH),
        signatureName: sanitizeInputFinal(name, MAX_NAME_LENGTH),
        signatureData: capturedSignatureData || '', // Small JPEG base64
        smsVerified: false,
        verificationMethod: 'id',
        verifiedContact: 'ID Required',
        phoneNumber: phoneNumber || '',
        deviceInfo: deviceInfo,
        pin: pin,
        idVerificationRequired: true,
        signedAt: new Date().toISOString(),
        waiverVersion: waiverVersion
      });

      // Track waiver completion for ad attribution
      if (trackingConfig.googleAdsId || trackingConfig.metaPixelId) {
        trackCompleteRegistration({
          googleAdsId: trackingConfig.googleAdsId,
          googleAdsLabel: trackingConfig.googleAdsLabel,
          metaPixelId: trackingConfig.metaPixelId,
          contentId: trip.confirmationCode || trip.id,
          contentName: `Waiver - ${organizationName || 'Booking'}`,
          customData: {
            waiver_version: waiverVersion,
            verification_method: 'id'
          }
        });
      }

      setSignedPin(pin);
      setShowSuccess(true);
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        if (status === 404) {
          toast.error('Booking not found. Please refresh and try again.');
        } else if (status === 403) {
          toast.error('Access denied. Please refresh and try again.');
        } else {
          toast.error(`Failed to sign (Error ${status}). Please try again.`);
        }
      } else {
        toast.error('Network error. Please try again.');
      }
    } finally {
      setSigning(false);
    }
  };

  // Auto-close timer for success screen
  const [countdown, setCountdown] = useState(5);
  
  useEffect(() => {
    if (showSuccess && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (showSuccess && countdown === 0) {
      window.location.reload();
    }
  }, [showSuccess, countdown]);

  // Success Screen Component
  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex flex-col">
        {/* Clean header */}
        <div className="bg-white p-4 sticky top-0 z-10">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">Waiver Signed</h3>

          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-sm mx-auto">
            {/* Success Icon */}
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            
            {/* Success Message */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Success!</h2>
            <p className="text-gray-600 mb-8">
              You have signed the waiver.
            </p>

            {/* ID Verification Warning */}
            {verificationMethod === 'id' && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">ID Required at Check-in</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Please bring a valid government-issued ID for verification.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Done Button with countdown */}
            <Button 
              onClick={() => window.location.reload()}
              className="w-full h-12 text-white text-lg font-semibold"
              style={{ background: brandColor }}
            >
              Done {countdown > 0 && `(${countdown}s)`}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col overflow-y-auto">
      {/* Header — logo or org name + progress bar */}
      <div className="bg-white p-4 pb-3 sticky top-0 z-10 border-b border-gray-100">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-2">
            {orgBranding?.logoUrl ? (
              <img
                src={orgBranding.logoUrl}
                alt={orgBranding.name || organizationName}
                className="h-8 w-auto max-w-[120px] object-contain rounded"
              />
            ) : (
              <span className="font-semibold text-gray-900" style={{ color: brandColor }}>
                {orgBranding?.name || organizationName || 'Sign Waiver'}
              </span>
            )}
          </div>
        </div>
        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className="h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${(Math.min(step, 5) / 5) * 100}%`, background: brandColor }}
          />
        </div>
      </div>

      <div className="flex-1 p-4 pb-24">
        <div className="max-w-md mx-auto">
          
          {/* STEP 1: Waiver Agreement */}
          {step === 1 && (
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold text-gray-900">Waiver</h2>
              </div>
            
            {!showWaiverText ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-50 flex items-center justify-center">
                  <Shield className="w-8 h-8 text-blue-600" />
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Please Review the Waiver</h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  By proceeding, you acknowledge that we collect your signature and device information for legal enforceability.
                </p>
                
                <Button 
                  onClick={() => {
                    setShowWaiverText(true);
                    setWaiverViewed(true);
                  }}
                  className="w-full h-12 text-white text-base font-semibold"
                  style={{ background: brandColor }}
                >
                  I Agree & View Waiver
                </Button>
              </div>
            ) : (
              <div>
                {/* Waiver text container */}
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 text-sm text-gray-700 leading-relaxed mb-4 max-h-80 overflow-y-auto whitespace-pre-wrap break-words">
                  {customWaiverText || trip.waiverText || 'Waiver text not configured. Please contact the charter operator.'}
                </div>
                
                {/* Checkbox for agreement */}
                <label className="flex items-start gap-3 mb-4 cursor-pointer group p-3 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
                  <input
                    type="checkbox"
                    checked={hasAgreedToWaiver}
                    onChange={(e) => setHasAgreedToWaiver(e.target.checked)}
                    className="mt-0.5 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 leading-relaxed">
                    I have read and agree to the <span className="font-semibold">Liability Waiver & Assumption of Risk</span>
                  </span>
                </label>
                
                <Button 
                  onClick={() => setStep(2)} 
                  disabled={!waiverViewed || !hasAgreedToWaiver} 
                  className="w-full h-12 text-white text-base font-semibold transition-all disabled:opacity-50"
                  style={{ background: hasAgreedToWaiver ? brandColor : '#9CA3AF' }}
                >
                  I Agree to the Liability Waiver
                </Button>
              </div>
            )}
          </div>
        )}

        {/* STEP 2: Name & Signature */}
        {step === 2 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Your Name & Signature</h2>
            <p className="text-sm text-gray-600 mb-4">Draw your signature and type your full legal name</p>
            
            {/* Signature Canvas - 1px border */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Signature <span className="text-red-500">*</span></label>
              <div className="border border-gray-300 rounded-xl bg-white overflow-hidden">
                <SignatureCanvas 
                  ref={signaturePadRef} 
                  canvasProps={{ 
                    className: 'w-full h-28', 
                    style: { touchAction: 'none' } 
                  }} 
                />
              </div>
              <button 
                onClick={() => signaturePadRef.current?.clear()} 
                className="mt-2 text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
              >
                Clear Signature
              </button>
            </div>
            
            {/* Name Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Legal Name <span className="text-red-500">*</span>
              </label>
              <Input 
                value={name} 
                onChange={(e) => {
                  const sanitized = sanitizeInput(e.target.value, MAX_NAME_LENGTH);
                  setName(sanitized);
                  if (sanitized.length >= MIN_NAME_LENGTH) validateName(sanitized);
                }}
                onBlur={() => name && validateName(name)}
                placeholder="Enter your full name" 
                className={`h-12 text-base ${nameError ? 'border-red-500 focus:ring-red-200' : ''}`}
                maxLength={MAX_NAME_LENGTH}
              />
              {nameError && (
                <p className="mt-1 text-xs text-red-600">{nameError}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">{name.length}/{MAX_NAME_LENGTH} characters</p>
            </div>
            
            {/* Navigation */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setStep(1)} 
                className="w-20 h-12 border-gray-200"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button 
                onClick={() => {
                  if (validateName(name)) {
                    // Just capture signature data (upload happens when sending verification code)
                    const sigData = captureSignatureLightweight();
                    setCapturedSignatureData(sigData);
                    // Reset verification method to show the selection screen
                    setVerificationMethod('');
                    setFailedMethods([]);
                    setStep(3);
                  }
                }} 
                disabled={name.trim().length < MIN_NAME_LENGTH} 
                className="flex-1 h-12 text-white text-base font-semibold disabled:opacity-50" 
                style={{ background: brandColor }}
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3: Verification Method Selection */}
        {step === 3 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Verify Your Identity</h2>
            <p className="text-sm text-gray-600 mb-4">
              Choose how you'd like to receive your verification code
            </p>
            
            {/* Method Selection - Only show unselected if neither has failed */}
            {!verificationMethod && (
              <div className="space-y-3 mb-4">
                <button
                  onClick={() => setVerificationMethod('email')}
                  className="w-full p-4 border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-gray-900">Email</div>
                    <div className="text-sm text-gray-500">Send code to your email</div>
                  </div>
                </button>
                
                <button
                  onClick={() => setVerificationMethod('phone')}
                  className="w-full p-4 border border-gray-200 rounded-xl hover:border-green-300 hover:bg-green-50 transition-all flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-gray-900">Phone (SMS)</div>
                    <div className="text-sm text-gray-500">Send code via text message</div>
                  </div>
                </button>
              </div>
            )}
            
            {/* Email Input - Show when email method selected */}
            {verificationMethod === 'email' && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <Input 
                    type="email" 
                    value={email} 
                    onChange={(e) => {
                      const sanitized = sanitizeInput(e.target.value.toLowerCase(), MAX_EMAIL_LENGTH);
                      setEmail(sanitized);
                      setVerificationFailed(false);
                      if (emailError) setEmailError('');
                    }}
                    onBlur={() => email && validateEmail(email)}
                    placeholder="your@email.com" 
                    className={`h-12 text-base ${emailError ? 'border-red-500 focus:ring-red-200' : ''}`}
                    maxLength={MAX_EMAIL_LENGTH}
                    autoComplete="email"
                  />
                  {emailError && (
                    <p className="mt-1 text-xs text-red-600">{emailError}</p>
                  )}
                </div>
                
                {/* Error Message */}
                {verificationFailed && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                    <p className="text-sm text-red-900 font-medium">Failed to send code</p>
                    <p className="text-xs text-red-700 mt-1">Check your email address or try another option below</p>
                  </div>
                )}
                
                {/* Fallback Options */}
                <div className="space-y-2 mb-4">
                  {!failedMethods.includes('phone') && (
                    <button 
                      onClick={() => {
                        setVerificationMethod('phone');
                        setVerificationFailed(false);
                        setFailedMethods([...failedMethods, 'email']);
                      }}
                      className="w-full text-sm py-2.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      Can't receive email? Try phone instead
                    </button>
                  )}
                  {failedMethods.includes('phone') && (
                    <button 
                      onClick={() => {
                        setSkippedVerification(true);
                        setVerificationMethod('id');
                        setStep(6);
                      }}
                      className="w-full text-sm py-2.5 text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded-lg transition-colors"
                    >
                      Can't receive verification? Verify with ID instead
                    </button>
                  )}
                </div>
                
                {/* Navigation */}
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setVerificationMethod('');
                      setVerificationFailed(false);
                    }} 
                    className="w-20 h-12 border-gray-200"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <Button 
                    onClick={handleSendCode} 
                    disabled={sendingCode || !isValidEmail(email)} 
                    className="flex-1 h-12 text-white text-base font-semibold disabled:opacity-50" 
                    style={{ background: brandColor }}
                  >
                    {sendingCode ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </span>
                    ) : 'Send Code'}
                  </Button>
                </div>
              </>
            )}
            
            {/* Phone Input - Show when phone method selected */}
            {verificationMethod === 'phone' && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <Input 
                    type="tel" 
                    value={phoneNumber} 
                    onChange={(e) => {
                      const sanitized = e.target.value.replace(/[^\d+\-() ]/g, '').slice(0, MAX_PHONE_LENGTH);
                      setPhoneNumber(sanitized);
                      setVerificationFailed(false);
                      if (phoneError) setPhoneError('');
                    }}
                    placeholder="+1 (555) 123-4567" 
                    className={`h-12 text-base ${phoneError ? 'border-red-500 focus:ring-red-200' : ''}`}
                    maxLength={MAX_PHONE_LENGTH}
                    autoComplete="tel"
                  />
                  {phoneError && (
                    <p className="mt-1 text-xs text-red-600">{phoneError}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-400">🇺🇸 US numbers only — if outside the US, please use email verification</p>
                </div>
                
                {/* Error Message */}
                {verificationFailed && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                    <p className="text-sm text-red-900 font-medium">Failed to send code</p>
                    <p className="text-xs text-red-700 mt-1">Check your phone number or try another option below</p>
                  </div>
                )}
                
                {/* Fallback Options */}
                <div className="space-y-2 mb-4">
                  {!failedMethods.includes('email') && (
                    <button 
                      onClick={() => {
                        setVerificationMethod('email');
                        setVerificationFailed(false);
                        setFailedMethods([...failedMethods, 'phone']);
                      }}
                      className="w-full text-sm py-2.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      Can't receive SMS? Try email instead
                    </button>
                  )}
                  {failedMethods.includes('email') && (
                    <button 
                      onClick={() => {
                        setSkippedVerification(true);
                        setVerificationMethod('id');
                        setStep(6);
                      }}
                      className="w-full text-sm py-2.5 text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded-lg transition-colors"
                    >
                      Can't receive verification? Verify with ID instead
                    </button>
                  )}
                </div>
                
                {/* Navigation */}
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setVerificationMethod('');
                      setVerificationFailed(false);
                    }} 
                    className="w-20 h-12 border-gray-200"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <Button 
                    onClick={handleSendCode} 
                    disabled={sendingCode || phoneNumber.length < 10} 
                    className="flex-1 h-12 text-white text-base font-semibold disabled:opacity-50" 
                    style={{ background: brandColor }}
                  >
                    {sendingCode ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </span>
                    ) : 'Send Code'}
                  </Button>
                </div>
              </>
            )}
            
            {/* Back button when no method selected */}
            {!verificationMethod && (
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(2)} 
                  className="w-20 h-12 border-gray-200"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* STEP 5: Enter Code */}
        {step === 5 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Enter Verification Code</h2>
            <p className="text-sm text-gray-600 mb-6">
              6-digit code sent to <span className="font-medium">{verificationMethod === 'email' ? email : phoneNumber}</span>
            </p>
            
            {/* 6-digit code input with auto-submit */}
            <CodeInput
              value={verificationMethod === 'email' ? emailCode : smsCode}
              onChange={(code) => {
                if (verificationMethod === 'email') {
                  setEmailCode(code);
                  latestEmailCodeRef.current = code;
                } else {
                  setSmsCode(code);
                  latestSmsCodeRef.current = code;
                }
              }}
              onComplete={(completedCode) => {
                // Auto-submit when 6 digits entered
                // Update refs immediately for handleSign to use
                if (verificationMethod === 'email') {
                  latestEmailCodeRef.current = completedCode;
                } else {
                  latestSmsCodeRef.current = completedCode;
                }
                if (!signing && completedCode.length === 6) {
                  handleSign();
                }
              }}
              disabled={signing}
            />
            
            {/* Resend / Fallback Options */}
            <div className="space-y-2 mb-6">
              <button 
                onClick={handleSendCode}
                disabled={resendCooldown > 0 || sendingCode}
                className="w-full text-sm py-2.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
              </button>
              
              {/* Show alternate method if one hasn't been tried */}
              {verificationMethod === 'email' && !failedMethods.includes('phone') && (
                <button 
                  onClick={() => {
                    setVerificationMethod('phone');
                    setFailedMethods([...failedMethods, 'email']);
                    setStep(3);
                  }}
                  className="w-full text-sm py-2.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  Try phone verification instead
                </button>
              )}
              {(verificationMethod === 'phone' || verificationMethod === 'sms') && !failedMethods.includes('email') && (
                <button 
                  onClick={() => {
                    setVerificationMethod('email');
                    setFailedMethods([...failedMethods, 'phone']);
                    setStep(3);
                  }}
                  className="w-full text-sm py-2.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  Try email verification instead
                </button>
              )}
              
              <button 
                onClick={() => {
                  setSkippedVerification(true);
                  setVerificationMethod('id');
                  setStep(6);
                }}
                className="w-full text-sm py-2.5 text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded-lg transition-colors"
              >
                Cannot receive code? Verify with ID
              </button>
            </div>
            
            {/* Navigation */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setStep(3)} 
                className="w-20 h-12 border-gray-200"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button 
                onClick={handleSign} 
                disabled={signing || (verificationMethod === 'email' ? emailCode.length !== 6 : (verificationMethod === 'phone' ? smsCode.length !== 6 : smsCode.length !== 6))} 
                className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white text-base font-semibold disabled:opacity-50"
              >
                {signing ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Signing...
                  </span>
                ) : 'Sign Waiver'}
              </Button>
            </div>
          </div>
        )}

        {/* STEP 6: ID Verification */}
        {step === 6 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
              <h2 className="text-xl font-bold text-amber-900">ID Verification Required</h2>
            </div>
            
            <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 mb-6">
              <p className="text-sm text-gray-800 mb-3">
                No problem! Our staff will verify your identity when you arrive.
              </p>
              <p className="text-sm text-gray-800 mb-3">
                <strong>Staff may take a photo of your government-issued ID.</strong> This serves as verification of your identity for the signed waiver.
              </p>
              <div className="flex items-start gap-2 pt-2 border-t border-amber-200">
                <Shield className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <p className="text-sm font-medium text-amber-900">
                  Bring a valid ID (driver license, passport, or state ID)
                </p>
              </div>
            </div>
            
            {/* Navigation */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setStep(skippedVerification ? 3 : 5)} 
                className="w-20 h-12 border-gray-200"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button 
                onClick={handleIdVerificationSign}
                disabled={signing}
                className="flex-1 h-12 bg-amber-600 hover:bg-amber-700 text-white text-base font-semibold disabled:opacity-50"
              >
                {signing ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Signing...
                  </span>
                ) : 'I Understand & Sign'}
              </Button>
            </div>
          </div>
        )}

        </div>
      </div>

      {/* Close button */}
      <button 
        onClick={onClose} 
        className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors z-20"
        aria-label="Close"
      >
        ✕
      </button>
    </div>
  );
};

export default MobileWaiverWizard;
